import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, resolveImage, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function CrossLinksSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const items = content.items || [];
    const variant = config.variant || 'cards';
    const columns = config.columns || 4;
    const cols = columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4';

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle };

    const Badge = ({ text }) => text ? (
        <span className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-bold text-white shadow-md"
            style={{ backgroundColor: s.accent }}>
            {text}
        </span>
    ) : null;

    // -- Horizontal variant --
    if (variant === 'horizontal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Tambien te puede interesar'} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : columns >= 3 ? 'md:grid-cols-2' : 'md:grid-cols-2'} gap-6`}>
                        {items.map((item, i) => {
                            const img = resolveImage(item, 'image', mediaFolders);
                            return (
                                <div key={i} className={`${card} overflow-hidden group transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <div className="flex flex-col sm:flex-row">
                                        {/* Image left */}
                                        <div className="relative sm:w-2/5 shrink-0">
                                            {img ? (
                                                <img src={img} alt={item.title || ''} loading="lazy" className="w-full h-full object-cover aspect-[4/3] sm:aspect-auto sm:min-h-full group-hover:scale-105 transition-transform duration-500" />
                                            ) : (
                                                <div className="w-full aspect-[4/3] sm:min-h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                                                    <svg className="w-10 h-10" style={{ color: hexToRgba(s.accent, 0.3) }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                </div>
                                            )}
                                            <Badge text={item.badge} />
                                        </div>
                                        {/* Content right */}
                                        <div className="p-5 flex flex-col justify-center flex-1">
                                            <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                            {item.description && <p className="text-sm leading-relaxed mb-4 line-clamp-3" style={{ color: s.textSub }}>{item.description}</p>}
                                            {item.link && (
                                                <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:underline" style={{ color: s.accent }}>
                                                    {item.cta_text || 'Ver mas'}
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // -- Overlay variant --
    if (variant === 'overlay') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Tambien te puede interesar'} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => {
                            const img = resolveImage(item, 'image', mediaFolders);
                            return (
                                <div key={i} className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <div className="aspect-[4/3] relative">
                                        {img ? (
                                            <img src={img} alt={item.title || ''} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                        ) : (
                                            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.7)})` }} />
                                        )}
                                        {/* Gradient overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                                        {/* Badge */}
                                        <Badge text={item.badge} />
                                        {/* Content at bottom */}
                                        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                            <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                                            {item.description && (
                                                <p className="text-sm text-white/80 leading-relaxed line-clamp-2 max-h-0 group-hover:max-h-20 opacity-0 group-hover:opacity-100 transition-all duration-500 overflow-hidden">
                                                    {item.description}
                                                </p>
                                            )}
                                            {item.link && (
                                                <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1.5 text-sm font-semibold mt-2 text-white hover:underline transition-colors">
                                                    {item.cta_text || 'Ver mas'}
                                                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // -- Cards variant (default) --
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Tambien te puede interesar'} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${cols} gap-6`}>
                    {items.map((item, i) => {
                        const img = resolveImage(item, 'image', mediaFolders);
                        return (
                            <div key={i} className={`${card} overflow-hidden group transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {/* Image */}
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    {img ? (
                                        <img src={img} alt={item.title || ''} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                                            <svg className="w-10 h-10" style={{ color: hexToRgba(s.accent, 0.3) }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                        </div>
                                    )}
                                    <Badge text={item.badge} />
                                </div>
                                {/* Content */}
                                <div className="p-5 flex flex-col flex-1">
                                    <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                    {item.description && <p className="text-sm leading-relaxed mb-4 line-clamp-3 flex-1" style={{ color: s.textSub }}>{item.description}</p>}
                                    {item.link && (
                                        <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:underline mt-auto" style={{ color: s.accent }}>
                                            {item.cta_text || 'Ver mas'}
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        </a>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
